<template>
  <div class="container-fluid p-0">
    <div class="row m-0">
      <div class="col-12 p-0">
        <div class="login-card login-dark">
          <div>
            <div>
              <a class="logo" href="#">
                <img
                    class="img-fluid for-light m-auto"
                    src="/assets/images/logo.svg"
                    alt="loginpage"
                />
                <img
                    class="img-fluid for-dark"
                    src="/assets/images/logo.svg"
                    alt="logo"
                />
              </a>
            </div>
            <div class="login-main wide-main position-relative">
              <form class="theme-form" @submit.prevent="handleOnboarding">
                <h2>Complete Your Registration</h2>
                <h6 class="mt-4 f-w-700">Confirm Your Information</h6>
                
                <div class="form-group">
                  <label class="col-form-label">First Name</label>
                  <input
                      class="form-control"
                      type="text"
                      v-model="firstName"
                      required
                      placeholder="John"
                  >
                </div>
                
                <div class="form-group">
                  <label class="col-form-label">Last Name</label>
                  <input
                      class="form-control"
                      type="text"
                      v-model="lastName"
                      required
                      placeholder="Doe"
                  >
                </div>
                
                <div class="form-group">
                  <label class="col-form-label">Phone Number</label>
                  <input
                      class="form-control"
                      type="tel"
                      v-model="phoneNumber"
                      required
                      placeholder="+1234567890"
                  >
                </div>

                <h6 class="mt-4 f-w-700">Create Your Password</h6>
                <div class="form-group">
                  <label class="col-form-label">New Password</label>
                  <div class="form-input position-relative">
                    <input
                        class="form-control"
                        :type="showPassword ? 'text' : 'password'"
                        v-model="newPassword"
                        required
                        placeholder="*********"
                    >
                    <div class="show-hide" @click="togglePassword">
                      <span :class="showPassword ? 'hide' : 'show'"></span>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label class="col-form-label">Confirm Password</label>
                  <input
                      class="form-control"
                      type="password"
                      v-model="confirmPassword"
                      required
                      placeholder="*********"
                  >
                </div>
                <div class="form-group mb-0 checkbox-checked">
                  <p v-if="errorMessage" class="text-danger fs-6">{{ errorMessage }}</p>
                  <button
                      class="btn btn-primary btn-block w-60 mt-3"
                      type="submit"
                      :disabled="loading"
                  >
                    {{ loading ? 'Submitting...' : 'Complete Registration' }}
                  </button>
                </div>
                <p class="mt-4 mb-0 text-center">
                  Already registered?<a class="ms-2" href="/">Sign in</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, watch} from 'vue';
import 'jquery-ui';

const props = defineProps({
  token: {type: String, required: true}
});

// Reactive state
const firstName = ref('');
const lastName = ref('');
const phoneNumber = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const loading = ref(false);
const showPassword = ref(false);
const errorMessage = ref(null);

// Clear error message when user starts typing in any field
watch([firstName, lastName, phoneNumber, newPassword, confirmPassword], () => {
  if (errorMessage.value) {
    errorMessage.value = null;
  }
});

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};



// Fetch user data on mount
onMounted(async () => {
  try {
    const response = await fetch(`/api/authenticate/user-info?token=${props.token}`);
    if (response.ok) {
      const userData = await response.json();
      firstName.value = userData.firstName || '';
      lastName.value = userData.lastName || '';
      phoneNumber.value = userData.phoneNumber || '';
    } else {
      errorMessage.value = 'Failed to load user information. Please try again.';
    }
  } catch (error) {
    console.error('Error fetching user data:', error);
    errorMessage.value = 'An error occurred while loading your information. Please try again later.';
  }
});

const handleOnboarding = async () => {
  if (loading.value) return;

  // Validate passwords match
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'Passwords do not match!';
    return;
  }

  loading.value = true;
  errorMessage.value = null;

  try {
    const response = await fetch('/api/authenticate/complete-dispatcher-onboarding', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firstName: firstName.value,
        lastName: lastName.value,
        phoneNumber: phoneNumber.value,
        newPassword: newPassword.value,
        token: props.token
      }),
    });

    if (response.redirected || response.ok) {
      // Redirect to login page upon success
      window.location.href = response.url
    } else {
      // If response is not OK, show error message
      const errorText = await response.text();
      errorMessage.value = errorText || 'Failed to complete registration. Please try again.';
    }
  } catch (error) {
    errorMessage.value = 'An error occurred. Please try again later.';
    console.error('Error completing registration:', error);
  } finally {
    loading.value = false;
  }
};
</script>